/**
 * TanStack's structural sharing, extended to `Date`s: keep every part of `prev` that `next`
 * equals, so a refetch that changed nothing hands the screen the SAME objects.
 *
 * ⚠️ Why it exists: TanStack's own `replaceEqualDeep` shares only plain objects and arrays.
 * A tRPC read through superjson brings back NEW `Date` instances on every fetch, so one date
 * anywhere in a row makes the row, its array and the whole answer new each time, and every
 * memo keyed on them recomputes: the crew map redrew all its markers every 60 s. Here two
 * dates are equal when their times are.
 *
 * Use as a query's `structuralSharing` option.
 */
export function shareEqualDeep<T>(prev: unknown, next: T): T {
  return share(prev, next) as T;
}

const isPlain = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && Object.getPrototypeOf(v) === Object.prototype;

function share(prev: unknown, next: unknown): unknown {
  if (prev === next) return prev;
  if (prev instanceof Date && next instanceof Date) {
    return prev.getTime() === next.getTime() ? prev : next;
  }
  if (Array.isArray(prev) && Array.isArray(next)) {
    const out = next.map((v, i) => share(prev[i], v));
    return out.length === prev.length && out.every((v, i) => v === prev[i]) ? prev : out;
  }
  if (isPlain(prev) && isPlain(next)) {
    const keys = Object.keys(next);
    const out: Record<string, unknown> = {};
    let same = keys.length === Object.keys(prev).length;
    for (const k of keys) {
      out[k] = share(prev[k], next[k]);
      if (out[k] !== prev[k] || !(k in prev)) same = false;
    }
    return same ? prev : out;
  }
  return next;
}
